// API Configuration
// This file centralizes API endpoint URLs

// Get API base URL from environment variable or use default
// This function is called at runtime to ensure window.location is available
export const getApiBaseUrl = (silent = false) => {
  // Check if we're in production and have a configured API URL
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Auto-detect backend URL based on current hostname and port
  // Use relative URLs so nginx reverse proxy handles routing to port 8445
  try {
    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
      const hostname = window.location.hostname;
      const port = window.location.port;
      
      // If accessing via localhost or 127.0.0.1, use direct connection for local development
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        const url = 'http://10.25.30.30:8445';
        return url;
      }
      
      // If accessing directly via port 8440 (not through Nginx on port 80), use direct backend URL
      if (port === '8440') {
        const url = `http://${hostname}:8445`;
        return url;
      }
      
      // For network access via port 80 (Nginx proxy), use relative URLs
      // Nginx on port 80 will proxy /api/agent/*, /api/admin/*, /api/employee/* requests to backend on port 8445
      return ''; // Empty string = relative URLs
    }
  } catch (e) {
    // Only log warning if not in silent mode
    if (!silent) {
      console.warn('⚠️ AgentBIMA API Config - Error detecting hostname:', e);
    }
  }
  
  // Fallback: Use relative URLs (nginx proxy)
  return ''; // Empty string = relative URLs
};

// Helper function to get full API URL
export const getApiUrl = (path) => {
  const baseUrl = getApiBaseUrl();
  return `${baseUrl}${path}`;
};

// API Endpoints - functions that compute URLs at runtime
export const API_ENDPOINTS = {
  // DMVIC Endpoints
  get issueCertificate() {
    return getApiUrl('/api/dmvic/issue-certificate');
  },
  get confirmCertificate() {
    return getApiUrl('/api/dmvic/confirm-certificate-issuance');
  },
  get getCertificateUrl() {
    return getApiUrl('/api/dmvic/get-certificate-url');
  },
  get updateCertificateStatus() {
    return getApiUrl('/api/dmvic/update-certificate-status');
  },
  certificateStatus: (clientId) => getApiUrl(`/api/dmvic/certificate-status/${clientId}`),
  /** BullMQ job status (when issue-certificate returns status: 'queued'). Pass clientId for DB fallback when queue is unavailable. */
  certificateJobStatus: (jobId, clientId = null) => {
    const base = getApiUrl(`/api/certificate-status/${jobId}`);
    if (clientId) return `${base}?clientId=${encodeURIComponent(clientId)}`;
    return base;
  },
  /** Retry a failed certificate job (re-queue by jobId) */
  certificateJobRetry: (jobId) => getApiUrl(`/api/certificate-status/${jobId}/retry`),
  /** Retry certificate by clientId (from agent dashboard; queues new job) */
  certificateRetryByClient: () => getApiUrl('/api/agent/certificate-retry'),
  get searchVehicle() {
    return getApiUrl('/api/dmvic/search-vehicle');
  },
  get validateDoubleInsurance() {
    return getApiUrl('/api/dmvic/validate-double-insurance');
  },
  
  // Payment Endpoints
  get initiateSTKPush() {
    return getApiUrl('/api/initiate-stkpush');
  },
  paymentStatus: (referenceNumber) => getApiUrl(`/api/payment-status/${referenceNumber}`),
  get verifyEcobankMpesaCode() {
    return getApiUrl('/api/ecobank/verify-mpesa-code');
  },
  get validateMpesaReference() {
    return getApiUrl('/api/payments/validate-mpesa-reference');
  },
  /** COMP UAT pilot: simulate successful payment (no Pesapal/Ecobank) */
  get simulateCompUatPayment() {
    return getApiUrl('/api/payments/simulate-comp-uat');
  },

  // Application Endpoints
  get submitApplication() {
    return getApiUrl('/api/submit-application');
  },
  /** Reserve cover after double-insurance check: allocate policy number and draft client for cover summary */
  get reserveCover() {
    return getApiUrl('/api/reserve-cover');
  },
  corePoliciesByRisk: (riskId) => getApiUrl(`/api/core/policies/risk/${encodeURIComponent(riskId)}`),
  extensionSeed: (policyNo) => {
    const key = String(policyNo || '').trim().replace(/\.+$/, '');
    return getApiUrl(`/api/extensions/seed?policyNo=${encodeURIComponent(key)}`);
  },
  extensionPortalPoliciesByRegistration: (riskId) =>
    getApiUrl(`/api/extensions/portal-policies-by-registration/${encodeURIComponent(riskId)}`),
  
  // IRA & Agent Endpoints
  get validateIRA() {
    return getApiUrl('/api/validate-ira');
  },
  get agentLogin() {
    return getApiUrl('/api/agent/login');
  },
  get agentBusiness() {
    return getApiUrl('/api/agent/business');
  },
  get agentCertificateRecovery() {
    return getApiUrl('/api/agent/certificate-recovery');
  },
  get agentProfile() {
    return getApiUrl('/api/agent/profile');
  },
  
  // ID Validation Endpoints
  get validateId() {
    return getApiUrl('/api/validate-id');
  },
  get validateKRA() {
    return getApiUrl('/api/validate-kra');
  },
  get validateBusinessRegistration() {
    return getApiUrl('/api/validate-business-registration');
  },
};

export default API_ENDPOINTS;

